import apiClient from '../client';

// The work schedule: who works which days, against the volume expected.
// The server does the arithmetic; this file is the shapes and the calls.
//
// NOTE: the shared apiClient's response interceptor returns response.data,
// so every call here resolves to the response BODY.

export const SCHEDULE_DAYS = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'] as const;
export type ScheduleDay = typeof SCHEDULE_DAYS[number];
export const DAY_LABEL: Record<ScheduleDay, string> = {
  sat: 'Sat', sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri',
};

export interface ScheduleDriver {
  accountId: string;
  /** Today's name, from the account. */
  name: string;
  /** The schedule is every current Driver and BC. */
  role?: 'Driver' | 'BC';
  /** False for someone new: nothing saved for them yet. */
  saved?: boolean;
  workArea?: string;
  maxStops: number;
  maxPackages: number;
  days: ScheduleDay[];
}

export interface ScheduleWorkArea {
  number: string;
  days: ScheduleDay[];
}

export interface WorkSchedule {
  drivers: ScheduleDriver[];
  workAreas: ScheduleWorkArea[];
  growthPercent: number;
}

export interface SuggestedDriver extends ScheduleDriver {
  fromStatements: boolean;
}

export interface ScheduleOverview {
  plan: WorkSchedule | null;
  suggestions: { drivers: SuggestedDriver[]; workAreas: ScheduleWorkArea[] };
  defaultGrowthPercent: number;
}

/** Delivery + e-commerce, the sheet's daily totals; pickups beside them. */
export interface DayVolume {
  stops: number;
  packages: number;
  pickupStops: number;
  pickupPackages: number;
}

export interface WeekDay {
  day: ScheduleDay;
  date: string;
  closed: boolean;
  scheduled: number;
  planned: number;
  spare: number;
  maxStops: number;
  maxPackages: number;
  volume: DayVolume | null;
}

export interface WeekDriver extends ScheduleDriver {
  changed: boolean;
  /** What the statement says they did, in a week that has one. */
  driven: Partial<Record<ScheduleDay, { stops: number; packages: number }>>;
}

export type WeekStatus = 'actual' | 'forecast' | 'none';

export interface ScheduleWeek {
  weekEnding: string;
  /** False until the company has set up its usual week. */
  plan: boolean;
  status: WeekStatus;
  growthPercent: number;
  weekGrowthPercent: number | null;
  closedDays: ScheduleDay[];
  workAreas: ScheduleWorkArea[];
  drivers: WeekDriver[];
  days: WeekDay[];
}

export interface TimelineDay extends DayVolume {
  day: ScheduleDay;
  date: string;
  maxStops: number | null;
  maxPackages: number | null;
}

export interface TimelineWeek {
  weekEnding: string;
  status: WeekStatus;
  stops: number | null;
  packages: number | null;
  pickupStops: number | null;
  pickupPackages: number | null;
  maxStops: number | null;
  maxPackages: number | null;
  lastYear: DayVolume | null;
  days: TimelineDay[];
}

export interface WeekChanges {
  drivers: { accountId: string; days: ScheduleDay[] }[];
  closedDays: ScheduleDay[];
  growthPercent: number | null;
}

class ScheduleService {
  private static readonly BASE = '/api/schedule';

  static async overview(): Promise<ScheduleOverview> {
    return apiClient.get(this.BASE) as any;
  }

  static async savePlan(plan: WorkSchedule): Promise<{ plan: WorkSchedule }> {
    return apiClient.put(this.BASE, plan) as any;
  }

  static async week(weekEnding: string): Promise<ScheduleWeek> {
    return apiClient.get(`${this.BASE}/weeks/${weekEnding}`) as any;
  }

  static async saveWeek(weekEnding: string, changes: WeekChanges): Promise<ScheduleWeek> {
    return apiClient.put(`${this.BASE}/weeks/${weekEnding}`, changes) as any;
  }

  static async timeline(): Promise<{ weeks: TimelineWeek[]; thisWeek: string }> {
    return apiClient.get(`${this.BASE}/timeline`) as any;
  }
}

export default ScheduleService;
